import { Injectable, Injector } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { AuthService } from '../auth/auth.service';
import { FunctionRegistryService } from '../function-registry/function-registry.service';
import { ComponentDataSourceService } from '../component-data-source/component-data-source.service';
import { environment } from '../../environment/environment';

// Import permission functions
import { canEditEvent } from './data/can-edit-event';
import { canViewEvent } from './data/can-view-event';
import { isEventInstructor } from './data/is-event-instructor';
import { canManageUsers } from './data/can-manage-users';
import { canViewAdminTools } from './data/can-view-admin-tools';
import { hasSpecificRole } from './data/has-specific-role';
import { canParticipateEvent } from './data/can-participate-event';
import { isGroupsEnabled } from './data/is-groups-enabled';

export interface PermissionFunction {
  name: string;
  description?: string;
  execute: (context?: any) => Observable<boolean>;
  settings?: any;
  requiresContext?: boolean;
}

export interface PermissionContext {
  eventId?: string;
  userId?: string;
  organizationId?: string;
  locationId?: string;
  pathId?: string;
  customData?: any;
  [key: string]: any;
}

@Injectable({
  providedIn: 'root'
})
export class ComponentPermissionService {
  private permissionRegistry = new Map<string, PermissionFunction>();
  private registeredPermissions: string[] = [];
  public readonly apiUrl = environment.apiUrl;

  constructor(
    public http: HttpClient,
    public authService: AuthService,
    private functionRegistryService: FunctionRegistryService,
    private componentDataSourceService: ComponentDataSourceService,
    public injector: Injector
  ) {
    // Register built-in permission functions
    this.registerPermission('canEditEvent', canEditEvent(this));
    this.registerPermission('canViewEvent', canViewEvent(this));
    this.registerPermission('isEventInstructor', isEventInstructor(this));
    this.registerPermission('canManageUsers', canManageUsers(this));
    this.registerPermission('canViewAdminTools', canViewAdminTools(this));
    this.registerPermission('hasSpecificRole', hasSpecificRole(this));
    this.registerPermission('canParticipateEvent', canParticipateEvent(this));
    this.registerPermission('isGroupsEnabled', isGroupsEnabled(this));

  }

  registerPermission(key: string, permissionFunction: PermissionFunction): void {
    
    if (!this.permissionRegistry.has(key) || key.includes('_RELOAD_')) {
      this.permissionRegistry.set(key, permissionFunction);
      
      if (!this.registeredPermissions.includes(key)) {
        this.registeredPermissions.push(key);
      }
    } else {
    }
  }

  getPermissionFunction(key: string): PermissionFunction | undefined {
    return this.permissionRegistry.get(key);
  }

  executePermission(key: string, context?: PermissionContext): Observable<boolean> {
    const permissionFunction = this.permissionRegistry.get(key);
    if (!permissionFunction) {
      console.error(`[ComponentPermissionService] Permission function '${key}' not found`);
      return of(false);
    }

    try {
      return permissionFunction.execute(context);
    } catch (error) {
      console.error(`[ComponentPermissionService] Error executing permission function '${key}':`, error);
      return of(false);
    }
  }

  getPermissionSettings(key: string): any | undefined {
    return this.permissionRegistry.get(key)?.settings;
  }

  getRegisteredPermissions(): string[] {
    return [...this.registeredPermissions];
  }

  getPermissionDescription(key: string): string | undefined {
    return this.permissionRegistry.get(key)?.description;
  }

  requiresContext(key: string): boolean {
    return this.permissionRegistry.get(key)?.requiresContext || false;
  }

  executeDataSource(key: string, ...args: any[]): Observable<any> | undefined {
    return this.componentDataSourceService.executeDataSource(key, ...args);
  }

  /**
   * Helper method to check multiple permissions at once
   */
  checkMultiplePermissions(permissions: string[], context?: PermissionContext): Observable<{ [key: string]: boolean }> {
    const results: { [key: string]: Observable<boolean> } = {};
    
    permissions.forEach(permission => {
      results[permission] = this.executePermission(permission, context);
    });
    
    // Convert to a single observable that emits all results
    return new Observable(observer => {
      const permissionResults: { [key: string]: boolean } = {};
      let completedCount = 0;
      
      Object.keys(results).forEach(key => {
        results[key].subscribe({
          next: (result) => {
            permissionResults[key] = result;
            completedCount++;
            
            if (completedCount === permissions.length) {
              observer.next(permissionResults);
              observer.complete();
            }
          },
          error: (error) => {
            console.error(`Error checking permission ${key}:`, error);
            permissionResults[key] = false;
            completedCount++;
            
            if (completedCount === permissions.length) {
              observer.next(permissionResults);
              observer.complete();
            }
          }
        });
      });
    });
  }
} 